/**
 * PHRASE BLOOM — 上昇/下降フレーズに特化した MIDI + オーディオ生成ツール。
 *
 * UI サウンド・ゲームの魔法エフェクト・遊技機効果音向けに、1〜2 小節の
 * 駆け上がり/駆け下がりフレーズの MIDI 土台を生成する実験的ツール。
 * パネルは Rupert Neve Designs Portico II へのオマージュ（白パネル）、
 * ノブの 5 色はサインインページ FlowingCircles のドットと同じ配色。
 *
 * メインフレーズに加え、SOUND A（ユニゾン/下ハモ）・SOUND B（ツイン =
 * メインと同じリズムで追走するエコーカノン。OFFSET はメイン最後の音基準の
 * 符号付き）を重ねられる。
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import toast from "react-hot-toast";

import IconButton from "../../../components/button/IconButton";
import ToolTip from "../../../components/tooltip/ToolTip";
import Knob from "./components/Knob";
import PanelSection from "./components/PanelSection";
import PianoRoll from "./components/PianoRoll";
import PresetBar from "./components/PresetBar";
import ToggleSwitch from "./components/ToggleSwitch";
import { useSynthEngine } from "./hooks/useSynthEngine";
import {
  PHRASE_BLOOM_SCHEMA_VERSION,
  PHRASE_BLOOM_TOOL_ID,
  buildPresetPayload,
  sanitizePresetPayload,
  type PhraseBloomPresetPayload,
} from "./utils/presetPayload";
import { NOTE_NAMES, SCALES, type CadenceId } from "./utils/musicTheory";
import {
  DEFAULT_PARAMS,
  END_POINTS,
  type PhraseParams,
} from "./utils/phraseGenerator";
import {
  DEFAULT_LAYER_PARAMS,
  TWIN_OFFSETS,
  TWIN_ZERO_OFFSET_INDEX,
  generateArrangement,
  type LayerParams,
  type SoundAMode,
} from "./utils/arrangement";
import { downloadBlob, downloadMidi, type MidiTrackData } from "./utils/midiWriter";
import { renderArrangementToWav } from "./utils/wavEncoder";
import {
  DEFAULT_SYNTH_PARAMS,
  DELAY_DIVISIONS,
  DELAY_MS_MAX,
  DELAY_MS_MIN,
  GLIDE_MS_MAX,
  GLIDE_MS_MIN,
  GOLD_RATE_MAX,
  GOLD_RATE_MIN,
  type OscType,
  type SynthParams,
} from "./utils/synthVoice";
import { BLOOM_COLORS, PANEL, PANEL_FONT } from "./palette";

const OSC_TYPES: { id: OscType; label: string }[] = [
  { id: "sine", label: "SINE" },
  { id: "triangle", label: "TRI" },
  { id: "sawtooth", label: "SAW" },
  { id: "square", label: "SQR" },
  { id: "noise", label: "NOISE" },
];

const CADENCES: { id: CadenceId; label: string }[] = [
  { id: "tonic", label: "TONIC" },
  { id: "subdominant", label: "SUBDOM" },
  { id: "dominant", label: "DOM" },
];

/** ？アイコンのホバーで表示するツールの説明（旧ページ頭の説明テキスト） */
const HELP_DESCRIPTION = [
  "上昇・下降フレーズに特化した MIDI / オーディオ生成ツール（実験的機能）。",
  "UI サウンド、魔法エフェクト、遊技機の創作効果音の土台づくりに。",
  "SOUND A（ユニゾン/下ハモ）・SOUND B（ツイン = メインと同じリズムで追走するエコー）を重ねられます。",
  "ツインの OFFSET はメイン最後の音を 0.0.0 とし、負で前倒し（重なり）、正で後ろへずらします。",
  "EFFECTS の KAITLYN は、ピッチ・音量・アタックへかすかな有機的揺らぎを加えます（書き出す WAV にも同じ揺らぎが入ります）。",
  "ノブは上下ドラッグまたはホイールで操作、Shift で微調整、ダブルクリックで初期値に戻ります。",
  "プリセットはパネル上部から保存でき、相互フォローのユーザーに自動で共有されます。",
].join("\n");

const SOUND_A_MODES: { id: SoundAMode; label: string }[] = [
  { id: "off", label: "OFF" },
  { id: "unison", label: "UNISON" },
  { id: "harmony", label: "HARMONY" },
];

// ---- フェイスプレート ----

const Faceplate = styled.div`
  width: 100%;
  border-radius: 14px;
  border: 1px solid ${PANEL.border};
  background: linear-gradient(165deg, ${PANEL.faceTop} 0%, ${PANEL.faceBottom} 100%);
  box-shadow:
    0 18px 40px rgba(10, 16, 24, 0.28),
    inset 0 1px 0 rgba(255, 255, 255, 0.85);
  padding: 28px 24px 24px;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 22px;
`;

/** 四隅のビス（溝の角度はコーナーごとに固定し、手締めのばらつきを演出） */
const SCREW_POSITIONS = {
  tl: { place: "top: 10px; left: 10px;", slot: 24 },
  tr: { place: "top: 10px; right: 10px;", slot: 118 },
  bl: { place: "bottom: 10px; left: 10px;", slot: 76 },
  br: { place: "bottom: 10px; right: 10px;", slot: 152 },
} as const;

const Screw = styled.span<{ $corner: keyof typeof SCREW_POSITIONS }>`
  position: absolute;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: radial-gradient(circle at 35% 30%, #eef3f7 0%, ${PANEL.screw} 45%, #5b6873 100%);
  box-shadow: inset 0 -1px 2px rgba(0, 0, 0, 0.45);
  ${({ $corner }) => SCREW_POSITIONS[$corner].place}

  /* マイナス溝 */
  &::after {
    content: "";
    position: absolute;
    top: 5px;
    left: 1.5px;
    right: 1.5px;
    height: 1.6px;
    background: rgba(20, 27, 35, 0.7);
    transform: rotate(${({ $corner }) => SCREW_POSITIONS[$corner].slot}deg);
  }
`;

const BrandRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 4px 16px;
`;

const BrandName = styled.h1`
  margin: 0;
  font-family: ${PANEL_FONT};
  font-weight: 800;
  font-size: 22px;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: ${PANEL.engravingStrong};
`;

const BrandSub = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: ${PANEL_FONT};
  font-weight: 800;
  font-size: 10px;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: ${PANEL.engraving};
`;

/** PC では最大 3 列で完結させる（1 列 → 2 列 → 3 列） */
const SectionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 18px 14px;
  align-items: stretch;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

// ---- トランスポート ----

const TransportRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 12px 18px;
  border-top: 1px solid ${PANEL.sectionBorder};
  padding-top: 18px;
`;

const HardButton = styled.button<{ $accent?: string; $active?: boolean }>`
  font-family: ${PANEL_FONT};
  font-weight: 800;
  font-size: 13px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${({ $active }) => ($active ? "#10161d" : "#eef3f7")};
  background: ${({ $active, $accent }) =>
    $active && $accent
      ? $accent
      : "linear-gradient(180deg, #333f4c 0%, #1d2630 100%)"};
  border: 1px solid ${PANEL.border};
  border-radius: 6px;
  padding: 10px 20px;
  min-width: 96px;
  cursor: pointer;
  box-shadow:
    0 3px 6px rgba(0, 0, 0, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.12);
  transition: transform 0.06s ease-out, box-shadow 0.06s ease-out;

  &:hover:not(:disabled) {
    filter: brightness(1.15);
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.08);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${PANEL.focus};
    outline-offset: 2px;
  }
`;

const SeedGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SeedLabel = styled.label`
  font-family: ${PANEL_FONT};
  font-weight: 800;
  font-size: 11px;
  letter-spacing: 0.14em;
  color: ${PANEL.engraving};
`;

const SeedInput = styled.input`
  width: 88px;
  font-family: ${PANEL_FONT};
  font-weight: 800;
  font-size: 14px;
  letter-spacing: 0.08em;
  text-align: center;
  color: ${PANEL.displayText};
  background: ${PANEL.displayBg};
  border: 1px solid ${PANEL.border};
  border-radius: 5px;
  padding: 8px 6px;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.7);

  &:focus {
    outline: 1px solid ${BLOOM_COLORS.cyan};
  }
`;

const DiceButton = styled(HardButton)`
  min-width: unset;
  padding: 8px 12px;
  font-size: 16px;
  line-height: 1;
`;

// ---- セクション内の非ノブ要素（ms 入力・サンプル読み込み） ----

const Field = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

const FieldLabel = styled.span`
  font-family: ${PANEL_FONT};
  font-weight: 800;
  font-size: 11px;
  letter-spacing: 0.12em;
  color: ${PANEL.engraving};
  text-transform: uppercase;
  white-space: nowrap;
`;

const MsInput = styled(SeedInput)`
  width: 76px;
`;

/** DELAY TIME (ms) の矢印キーステップ（Shift 併用で粗く動かす） */
const DELAY_MS_STEP = 1;
const DELAY_MS_STEP_COARSE = 10;

const clampDelayMs = (ms: number): number =>
  Math.min(DELAY_MS_MAX, Math.max(DELAY_MS_MIN, Math.round(ms)));

// ---- ページ本体 ----

const PhraseBloom: React.FC = () => {
  const [params, setParams] = useState<PhraseParams>(DEFAULT_PARAMS);
  const [layerParams, setLayerParams] = useState<LayerParams>(DEFAULT_LAYER_PARAMS);
  const [synthParams, setSynthParams] = useState<SynthParams>(DEFAULT_SYNTH_PARAMS);
  const [isRenderingWav, setIsRenderingWav] = useState(false);

  const arrangement = useMemo(
    () => generateArrangement(params, layerParams),
    [params, layerParams],
  );

  const engine = useSynthEngine({
    arrangement,
    bpm: params.bpm,
    synthParams,
  });

  const setParam = useCallback(
    <K extends keyof PhraseParams>(key: K, value: PhraseParams[K]) => {
      setParams((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const setLayerParam = useCallback(
    <K extends keyof LayerParams>(key: K, value: LayerParams[K]) => {
      setLayerParams((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const setSynthParam = useCallback(
    <K extends keyof SynthParams>(key: K, value: SynthParams[K]) => {
      setSynthParams((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const rollSeed = useCallback(() => {
    setParam("seed", Math.floor(Math.random() * 100000));
  }, [setParam]);

  // ---- DELAY TIME (ms) の下書き入力 ----
  // 制御された数値入力だと「全部消して打ち直す」操作が破綻するため、
  // 入力中は文字列の下書きを保持し、確定（blur / Enter）時にクランプする。

  const [delayMsDraft, setDelayMsDraft] = useState(
    String(DEFAULT_SYNTH_PARAMS.delayMs),
  );

  // プリセット読込など外部からの値変更を下書きへ同期する
  useEffect(() => {
    setDelayMsDraft((prev) =>
      Number(prev) === synthParams.delayMs ? prev : String(synthParams.delayMs),
    );
  }, [synthParams.delayMs]);

  const handleDelayMsInput = useCallback(
    (raw: string) => {
      setDelayMsDraft(raw);
      // 打っている最中も耳で確かめられるよう、数値として読める間は即反映する。
      // ここでクランプしないのは、下書きを書き換えるとキャレットが飛ぶため
      // （範囲外の値は delayTimeSeconds 側で丸まるので、確定時のクランプで足りる）。
      const parsed = Number(raw);
      if (raw.trim() !== "" && Number.isFinite(parsed)) {
        setSynthParam("delayMs", Math.round(parsed));
      }
    },
    [setSynthParam],
  );

  const commitDelayMs = useCallback(() => {
    const parsed = Number(delayMsDraft);
    // 空欄・数値にならない入力は直前の確定値へ戻す
    const next =
      delayMsDraft.trim() !== "" && Number.isFinite(parsed)
        ? clampDelayMs(parsed)
        : synthParams.delayMs;
    setSynthParam("delayMs", next);
    setDelayMsDraft(String(next));
  }, [delayMsDraft, synthParams.delayMs, setSynthParam]);

  const stepDelayMs = useCallback(
    (direction: 1 | -1, coarse: boolean) => {
      const step = coarse ? DELAY_MS_STEP_COARSE : DELAY_MS_STEP;
      const next = clampDelayMs(synthParams.delayMs + direction * step);
      setSynthParam("delayMs", next);
      setDelayMsDraft(String(next));
    },
    [synthParams.delayMs, setSynthParam],
  );

  const handleDelayMsKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        commitDelayMs();
      } else if (e.key === "Escape") {
        setDelayMsDraft(String(synthParams.delayMs));
      } else if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        e.preventDefault();
        stepDelayMs(e.key === "ArrowUp" ? 1 : -1, e.shiftKey);
      }
    },
    [commitDelayMs, stepDelayMs, synthParams.delayMs],
  );

  // ---- プリセット（ツールプリセット基盤との接続） ----

  const getPresetPayload = useCallback(
    () => buildPresetPayload(params, layerParams, synthParams),
    [params, layerParams, synthParams],
  );

  const applyPresetPayload = useCallback(
    (payload: unknown): PhraseBloomPresetPayload | null => {
      const sanitized = sanitizePresetPayload(payload);
      if (!sanitized) return null;
      setParams(sanitized.params);
      setLayerParams(sanitized.layerParams);
      setSynthParams(sanitized.synthParams);
      // 適用した正規化済みペイロードを返し、プリセットバーの
      // ダーティ判定スナップショットに使ってもらう。
      return sanitized;
    },
    [],
  );

  // BPM-NOTES-SCALE を軸にした保存名。MIDI/WAV のファイル名と
  // プリセットの自動名（未保存時の表示名）で共有する。
  const scaleLabel = useMemo(
    () => SCALES.find((s) => s.id === params.scaleId)?.label ?? "",
    [params.scaleId],
  );

  const filenameBase = useMemo(() => {
    const key = `${NOTE_NAMES[params.rootPc]}${params.baseOctave}`.replace("#", "s");
    const scale = scaleLabel.toLowerCase().replace(/[^a-z0-9]+/g, "-");
    return `bloom_${params.bpm}bpm-${params.noteCount}notes-${scale}_${key}_seed${params.seed}`;
  }, [
    params.bpm,
    params.noteCount,
    params.rootPc,
    params.baseOctave,
    params.seed,
    scaleLabel,
  ]);

  /** 未保存プリセットの自動名（例: 140BPM-12NOTES-MAJ PENTA） */
  const defaultPresetName = useMemo(
    () => `${params.bpm}BPM-${params.noteCount}NOTES-${scaleLabel}`,
    [params.bpm, params.noteCount, scaleLabel],
  );

  const handleDownloadMidi = useCallback(() => {
    const tracks: MidiTrackData[] = [
      { name: "MAIN", channel: 0, notes: arrangement.main },
    ];
    if (arrangement.soundA.length > 0) {
      tracks.push({ name: "SOUND A", channel: 1, notes: arrangement.soundA });
    }
    if (arrangement.soundB.length > 0) {
      tracks.push({ name: "SOUND B TWIN", channel: 2, notes: arrangement.soundB });
    }
    downloadMidi(tracks, params.bpm, `${filenameBase}.mid`);
  }, [arrangement, params.bpm, filenameBase]);

  const handleDownloadWav = useCallback(async () => {
    setIsRenderingWav(true);
    try {
      const wav = await renderArrangementToWav(arrangement, params.bpm, synthParams);
      downloadBlob(wav, `${filenameBase}.wav`, "audio/wav");
    } catch (e) {
      console.error("WAV 書き出しに失敗:", e);
      toast.error("WAV の書き出しに失敗しました");
    } finally {
      setIsRenderingWav(false);
    }
  }, [arrangement, params.bpm, synthParams, filenameBase]);

  // ステップセレクター用のインデックス値
  const endPointIndex = Math.max(
    0,
    END_POINTS.findIndex((e) => e.id === params.endPointId),
  );
  const scaleIndex = Math.max(
    0,
    SCALES.findIndex((s) => s.id === params.scaleId),
  );
  const cadenceIndex = Math.max(
    0,
    CADENCES.findIndex((c) => c.id === params.cadence),
  );
  const oscIndex = Math.max(
    0,
    OSC_TYPES.findIndex((o) => o.id === synthParams.osc),
  );
  const soundAModeIndex = Math.max(
    0,
    SOUND_A_MODES.findIndex((m) => m.id === layerParams.soundAMode),
  );
  const soundAOscIndex = Math.max(
    0,
    OSC_TYPES.findIndex((o) => o.id === synthParams.soundAOsc),
  );
  const soundBOscIndex = Math.max(
    0,
    OSC_TYPES.findIndex((o) => o.id === synthParams.soundBOsc),
  );
  const twinOffsetIndex = Math.max(
    0,
    TWIN_OFFSETS.findIndex((o) => o.id === layerParams.soundBOffsetId),
  );
  const delayDivIndex = Math.max(
    0,
    DELAY_DIVISIONS.findIndex((d) => d.id === synthParams.delayDivisionId),
  );

  return (
    <div className="u-col-center u-gap16" style={{ paddingBlock: 24 }}>
      <div className="u-wrapper">
        <Faceplate>
          <Screw $corner="tl" />
          <Screw $corner="tr" />
          <Screw $corner="bl" />
          <Screw $corner="br" />

          <BrandRow>
            <BrandName>Phrase Bloom</BrandName>
            <BrandSub>
              Rise &amp; Fall Midi Generator — Soremo Labo
              <ToolTip description={HELP_DESCRIPTION} placement="bottom-end">
                <span>
                  <IconButton
                    icon="help_outline"
                    size={18}
                    ariaLabel="ツールの使い方"
                  />
                </span>
              </ToolTip>
            </BrandSub>
          </BrandRow>

          <PresetBar
            toolId={PHRASE_BLOOM_TOOL_ID}
            schemaVersion={PHRASE_BLOOM_SCHEMA_VERSION}
            defaultName={defaultPresetName}
            getPayload={getPresetPayload}
            applyPayload={applyPresetPayload}
          />

          <SectionsGrid>
            <PanelSection title="Time">
              <Knob
                label="BPM"
                value={params.bpm}
                min={60}
                max={240}
                defaultValue={DEFAULT_PARAMS.bpm}
                onChange={(v) => setParam("bpm", v)}
                color={BLOOM_COLORS.blue}
              />
              <Knob
                label="Notes"
                value={params.noteCount}
                min={2}
                max={32}
                defaultValue={DEFAULT_PARAMS.noteCount}
                onChange={(v) => setParam("noteCount", v)}
                color={BLOOM_COLORS.blue}
              />
              <Knob
                label="End Point"
                value={endPointIndex}
                min={0}
                max={END_POINTS.length - 1}
                defaultValue={3}
                detents={END_POINTS.length}
                format={(i) => END_POINTS[i]?.label ?? ""}
                onChange={(i) => setParam("endPointId", END_POINTS[i].id)}
                color={BLOOM_COLORS.blue}
              />
              <Knob
                label="Accel"
                value={params.accel}
                min={-50}
                max={50}
                defaultValue={DEFAULT_PARAMS.accel}
                bipolar
                format={(v) => `${v > 0 ? "+" : ""}${v}%`}
                onChange={(v) => setParam("accel", v)}
                color={BLOOM_COLORS.blue}
              />
            </PanelSection>

            <PanelSection title="Phrase">
              <Knob
                label="Rise"
                value={params.riseWeight}
                min={0}
                max={100}
                defaultValue={DEFAULT_PARAMS.riseWeight}
                format={(v) => `${v}`}
                onChange={(v) => setParam("riseWeight", v)}
                color={BLOOM_COLORS.yellow}
              />
              <Knob
                label="Keep"
                value={params.keepWeight}
                min={0}
                max={100}
                defaultValue={DEFAULT_PARAMS.keepWeight}
                format={(v) => `${v}`}
                onChange={(v) => setParam("keepWeight", v)}
                color={BLOOM_COLORS.yellow}
              />
              <Knob
                label="Fall"
                value={params.fallWeight}
                min={0}
                max={100}
                defaultValue={DEFAULT_PARAMS.fallWeight}
                format={(v) => `${v}`}
                onChange={(v) => setParam("fallWeight", v)}
                color={BLOOM_COLORS.yellow}
              />
              <Knob
                label="Gate"
                value={params.gate}
                min={10}
                max={200}
                defaultValue={DEFAULT_PARAMS.gate}
                format={(v) => `${v}%`}
                onChange={(v) => setParam("gate", v)}
                color={BLOOM_COLORS.cyan}
              />
              <ToggleSwitch
                label="Glide"
                value={params.glide}
                onChange={(v) => setParam("glide", v)}
                color={BLOOM_COLORS.cyan}
              />
              <Knob
                label="G Time"
                value={synthParams.glideMs}
                min={GLIDE_MS_MIN}
                max={GLIDE_MS_MAX}
                defaultValue={DEFAULT_SYNTH_PARAMS.glideMs}
                format={(v) => `${v}ms`}
                onChange={(v) => setSynthParam("glideMs", v)}
                color={BLOOM_COLORS.cyan}
              />
            </PanelSection>

            <PanelSection title="Pitch">
              <Knob
                label="Key"
                value={params.rootPc}
                min={0}
                max={11}
                defaultValue={DEFAULT_PARAMS.rootPc}
                detents={12}
                format={(i) => NOTE_NAMES[i] ?? ""}
                onChange={(v) => setParam("rootPc", v)}
                color={BLOOM_COLORS.red}
              />
              <Knob
                label="Octave"
                value={params.baseOctave}
                min={0}
                max={6}
                defaultValue={DEFAULT_PARAMS.baseOctave}
                detents={7}
                format={(v) => `C${v}`}
                onChange={(v) => setParam("baseOctave", v)}
                color={BLOOM_COLORS.red}
              />
              <Knob
                label="Scale"
                value={scaleIndex}
                min={0}
                max={SCALES.length - 1}
                defaultValue={Math.max(
                  0,
                  SCALES.findIndex((s) => s.id === DEFAULT_PARAMS.scaleId),
                )}
                detents={SCALES.length}
                format={(i) => SCALES[i]?.label ?? ""}
                onChange={(i) => setParam("scaleId", SCALES[i].id)}
                color={BLOOM_COLORS.red}
              />
              <Knob
                label="Cadence"
                value={cadenceIndex}
                min={0}
                max={CADENCES.length - 1}
                defaultValue={0}
                detents={CADENCES.length}
                format={(i) => CADENCES[i]?.label ?? ""}
                onChange={(i) => setParam("cadence", CADENCES[i].id)}
                color={BLOOM_COLORS.red}
              />
            </PanelSection>

            <PanelSection title="Sound / A">
              <Knob
                label="Osc"
                value={oscIndex}
                min={0}
                max={OSC_TYPES.length - 1}
                defaultValue={3}
                detents={OSC_TYPES.length}
                format={(i) => OSC_TYPES[i]?.label ?? ""}
                onChange={(i) => setSynthParam("osc", OSC_TYPES[i].id)}
                color={BLOOM_COLORS.cyan}
              />
              <ToggleSwitch
                label="Gold"
                value={synthParams.goldOn}
                onChange={(v) => setSynthParam("goldOn", v)}
                color={BLOOM_COLORS.yellow}
              />
              <Knob
                label="G Rate"
                value={synthParams.goldRateHz}
                min={GOLD_RATE_MIN}
                max={GOLD_RATE_MAX}
                defaultValue={DEFAULT_SYNTH_PARAMS.goldRateHz}
                format={(v) => `${v}Hz`}
                onChange={(v) => setSynthParam("goldRateHz", v)}
                color={BLOOM_COLORS.yellow}
              />
              <Knob
                label="A Mode"
                value={soundAModeIndex}
                min={0}
                max={SOUND_A_MODES.length - 1}
                defaultValue={0}
                detents={SOUND_A_MODES.length}
                format={(i) => SOUND_A_MODES[i]?.label ?? ""}
                onChange={(i) => setLayerParam("soundAMode", SOUND_A_MODES[i].id)}
                color={BLOOM_COLORS.blue}
              />
              <Knob
                label="A Osc"
                value={soundAOscIndex}
                min={0}
                max={OSC_TYPES.length - 1}
                defaultValue={0}
                detents={OSC_TYPES.length}
                format={(i) => OSC_TYPES[i]?.label ?? ""}
                onChange={(i) => setSynthParam("soundAOsc", OSC_TYPES[i].id)}
                color={BLOOM_COLORS.blue}
              />
            </PanelSection>

            <PanelSection title="Sound B / Twin">
              <ToggleSwitch
                label="Twin"
                value={layerParams.soundBEnabled}
                onChange={(v) => setLayerParam("soundBEnabled", v)}
                color={BLOOM_COLORS.orange}
              />
              <Knob
                label="Offset"
                value={twinOffsetIndex}
                min={0}
                max={TWIN_OFFSETS.length - 1}
                defaultValue={TWIN_ZERO_OFFSET_INDEX}
                detents={TWIN_OFFSETS.length}
                bipolar
                format={(i) => TWIN_OFFSETS[i]?.label ?? ""}
                onChange={(i) => setLayerParam("soundBOffsetId", TWIN_OFFSETS[i].id)}
                color={BLOOM_COLORS.orange}
              />
              <Knob
                label="B Osc"
                value={soundBOscIndex}
                min={0}
                max={OSC_TYPES.length - 1}
                defaultValue={1}
                detents={OSC_TYPES.length}
                format={(i) => OSC_TYPES[i]?.label ?? ""}
                onChange={(i) => setSynthParam("soundBOsc", OSC_TYPES[i].id)}
                color={BLOOM_COLORS.orange}
              />
            </PanelSection>

            <PanelSection title="Effects">
              <ToggleSwitch
                label="MS Mode"
                value={synthParams.delayMode === "ms"}
                onChange={(v) => setSynthParam("delayMode", v ? "ms" : "sync")}
                color={BLOOM_COLORS.orange}
              />
              {synthParams.delayMode === "sync" ? (
                <Knob
                  label="Dly Time"
                  value={delayDivIndex}
                  min={0}
                  max={DELAY_DIVISIONS.length - 1}
                  defaultValue={3}
                  detents={DELAY_DIVISIONS.length}
                  format={(i) => DELAY_DIVISIONS[i]?.label ?? ""}
                  onChange={(i) =>
                    setSynthParam("delayDivisionId", DELAY_DIVISIONS[i].id)
                  }
                  color={BLOOM_COLORS.orange}
                />
              ) : (
                <Field>
                  <FieldLabel>Dly Time</FieldLabel>
                  <MsInput
                    id="bloom-delay-ms"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    aria-label="ディレイタイム（ミリ秒）"
                    value={delayMsDraft}
                    onChange={(e) => handleDelayMsInput(e.target.value)}
                    onBlur={commitDelayMs}
                    onKeyDown={handleDelayMsKeyDown}
                  />
                  <FieldLabel>ms</FieldLabel>
                </Field>
              )}
              <Knob
                label="Feedback"
                value={Math.round(synthParams.delayFeedback * 100)}
                min={0}
                max={90}
                defaultValue={35}
                format={(v) => `${v}%`}
                onChange={(v) => setSynthParam("delayFeedback", v / 100)}
                color={BLOOM_COLORS.orange}
              />
              <Knob
                label="Dly Mix"
                value={Math.round(synthParams.delayMix * 100)}
                min={0}
                max={100}
                defaultValue={25}
                format={(v) => `${v}%`}
                onChange={(v) => setSynthParam("delayMix", v / 100)}
                color={BLOOM_COLORS.orange}
              />
              <ToggleSwitch
                label="Clip"
                value={synthParams.clipOn}
                onChange={(v) => setSynthParam("clipOn", v)}
                color={BLOOM_COLORS.red}
              />
              <Knob
                label="Drive"
                value={synthParams.clipDrive}
                min={0}
                max={24}
                defaultValue={6}
                format={(v) => `${v}dB`}
                onChange={(v) => setSynthParam("clipDrive", v)}
                color={BLOOM_COLORS.red}
              />
              <ToggleSwitch
                label="Kaitlyn"
                value={synthParams.kaitlynOn}
                onChange={(v) => setSynthParam("kaitlynOn", v)}
                color={BLOOM_COLORS.cyan}
              />
            </PanelSection>
          </SectionsGrid>

          <TransportRow>
            <SeedGroup>
              <SeedLabel htmlFor="bloom-seed">Seed</SeedLabel>
              <SeedInput
                id="bloom-seed"
                type="number"
                min={0}
                max={999999}
                value={params.seed}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  if (Number.isFinite(v)) setParam("seed", Math.max(0, Math.floor(v)));
                }}
              />
              <DiceButton type="button" onClick={rollSeed} aria-label="シードをランダムに変更">
                &#x2684;
              </DiceButton>
            </SeedGroup>

            <HardButton
              type="button"
              $accent={BLOOM_COLORS.yellow}
              $active={engine.isPlaying}
              onClick={engine.isPlaying ? engine.stop : engine.play}
            >
              {engine.isPlaying ? "Stop" : "Play"}
            </HardButton>

            <HardButton type="button" onClick={handleDownloadMidi}>
              MIDI ↓
            </HardButton>
            <HardButton
              type="button"
              onClick={handleDownloadWav}
              disabled={isRenderingWav}
            >
              {isRenderingWav ? "Rendering…" : "WAV ↓"}
            </HardButton>
          </TransportRow>

          <PianoRoll arrangement={arrangement} playheadTick={engine.playheadTick} />
        </Faceplate>
      </div>
    </div>
  );
};

export default PhraseBloom;
